import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Col, Container, Row, Card, CardBody, NavItem, NavLink, Nav, TabPane, Button, TabContent, Input} from 'reactstrap';
import {Link, useParams} from 'react-router-dom'
import axios from 'axios'
import {QueryClient, QueryClientProvider, useQuery} from 'react-query'
import {connect} from 'react-redux'
import {withRouter} from 'react-router-dom'
import {isEmpty} from '@/shared/helpers'
import {useExpanded, useFlexLayout, usePagination, useResizeColumns, useSortBy, useTable} from "react-table";
import {USERS_COLUMNS} from "../columns";
import ReactTablePagination from '@/shared/components/table/components/ReactTablePagination';
import {reducer, useEffectToDispatch, fetchApiData, initialState, TableFilter} from "@/shared/apiTableHelper"
import {useDispatch, sendFlashMessage} from "@/shared/components/flash"
import ApiErrorMessage from "@/shared/components/ApiErrorMessage"
import SortIcon from "mdi-react/SortIcon";
import SortAscendingIcon from "mdi-react/SortAscendingIcon";
import SortDescendingIcon from "mdi-react/SortDescendingIcon";
import {toCurrency, toPoints} from '@/shared/helpers'

const queryClient = new QueryClient()

const ProgramViewUserGiftCodesRedeemed = ({organization, program, user}) => {

    const [filter, setFilter] = useState({keyword: ''});
    const [useFilter, setUseFilter] = useState(false);
    const [trigger, setTrigger] = useState(Math.floor(Date.now() / 1000));

    const dispatcher = useDispatch();

    let {programId, userId} = useParams();

    const defaultColumn = React.useMemo(
        () => ({
            maxWidth: 400,
        }),
        []
    )

    const [{
        queryPageIndex,
        queryPageSize,
        totalCount,
        queryPageFilter,
        queryPageSortBy,
        queryTrigger
    }, dispatch] = React.useReducer(reducer, initialState);

    const apiUrl = `/organization/${program.organization_id}/program/${program.id}/user/${user.id}/gift-codes-redeemed`
    const {isLoading, error, data, isSuccess, refetch} = useQuery(
        ['giftCodesRedeemed', queryPageIndex, queryPageSize, queryPageFilter, queryPageSortBy, queryTrigger],
        () => fetchApiData(
            {
                url: apiUrl,
                page: queryPageIndex,
                size: queryPageSize,
                filter: queryPageFilter,
                sortby: queryPageSortBy,
                trigger: queryTrigger
            }),
        {
            keepPreviousData: true,
            staleTime: Infinity,
        }
    );

    const handleRefund = useCallback(async (giftcodeId, reason) => {
        try {
            const response = await axios.post(
                `/organization/${program.organization_id}/program/${program.id}/user/${user.id}/gift-codes-redeemed/${giftcodeId}/refund`,
                {reason}
            );

            if (response?.data?.success === false) {
                dispatcher(sendFlashMessage(response.data.error || 'Failed to refund gift code', 'alert-danger'));
                return;
            }

            dispatcher(sendFlashMessage('Gift code refunded successfully', 'alert-success'));
            refetch();
        } catch (e) {
            const errorMsg = e.response?.data?.error || e.response?.data?.message || 'Failed to refund gift code';
            dispatcher(sendFlashMessage(errorMsg, 'alert-danger'));
        }
    }, [program.organization_id, program.id, user.id, dispatcher, refetch]);

    // columns depends on handleRefund so it recomputes if org/program/user ever change
    // (e.g. this component gets reused across a route param change without remounting).
    // This is still safe from the original per-keystroke re-render bug: the Refund
    // cell's reason text lives in RefundCell's own local state (see below), so typing
    // never triggers a parent re-render and never touches this memo.
    let columns = useMemo(
        () => buildGiftCodesRedeemedColumns({onRefund: handleRefund}),
        [handleRefund]
    )

    const totalPageCount = Math.ceil(totalCount / queryPageSize)
    // console.log(data)

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        page,
        pageCount,
        pageOptions,
        gotoPage,
        previousPage,
        canPreviousPage,
        nextPage,
        canNextPage,
        setPageSize,
        state: {pageIndex, pageSize, sortBy}
    } = useTable({
            columns,
            data: data ? data.results : [],
            initialState: {
                pageIndex: queryPageIndex,
                pageSize: queryPageSize,
                sortBy: queryPageSortBy,
            },
            manualPagination: true, // Tell the usePagination
            manualSortBy: true,
            pageCount: data ? totalPageCount : 0,
            autoResetSortBy: false,
            autoResetExpanded: false,
            autoResetPage: false,
            defaultColumn,

        },
        useSortBy,
        useExpanded,
        usePagination,
        useResizeColumns,
        useFlexLayout,
    );
    const manualPageSize = []

    useEffectToDispatch(dispatch, {pageIndex, pageSize, gotoPage, sortBy, filter, data, useFilter, trigger});

    if (!program?.id || !organization?.id || !data) {
        return <p>Loading...</p>;
    }

    // console.log(data)
    return (
        <>
            <Row>
                <Col md={12}>
                    <table {...getTableProps()} className="table">
                        <thead>
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map(column => (
                                    <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                                        {column.render('Header')}
                                        {column.isSorted ? <Sorting column={column}/> : ''}
                                    </th>
                                ))}
                            </tr>
                        ))}
                        </thead>
                        <tbody className="table table--bordered" {...getTableBodyProps()}>
                        {page.map(row => {
                            prepareRow(row);
                            const subCount = (row.id.match(/\./g) || []).length
                            return (
                                <tr {...row.getRowProps()}>
                                    {
                                        row.cells.map(cell => {
                                            const paddingLeft = subCount * 20
                                            return <td {...cell.getCellProps()}><span
                                                style={cell.column.Header === '#' ? {paddingLeft: `${paddingLeft}px`} : null}>{cell.render('Cell')}</span>
                                            </td>
                                        })
                                    }
                                </tr>
                            )
                        })}
                        </tbody>
                    </table>
                </Col>
            </Row>
            <Row>
                <Col md={12}>
                    <p>&nbsp;</p>
                    {(rows.length > 0) && (
                        <>
                            <ReactTablePagination
                                page={page}
                                gotoPage={gotoPage}
                                previousPage={previousPage}
                                nextPage={nextPage}
                                canPreviousPage={canPreviousPage}
                                canNextPage={canNextPage}
                                pageOptions={pageOptions}
                                pageSize={pageSize}
                                pageIndex={pageIndex}
                                pageCount={pageCount}
                                setPageSize={setPageSize}
                                manualPageSize={manualPageSize}
                                dataLength={totalCount}
                            />
                            <div className="pagination justify-content-end mt-2">
                          <span>
                          Go to page:{' '}
                              <input
                                  type="number"
                                  value={pageIndex + 1}
                                  onChange={(e) => {
                                      const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                      gotoPage(page);
                                  }}
                                  style={{ width: '100px' }}
                              />
                          </span>{' '}
                                <select
                                    value={pageSize}
                                    onChange={(e) => {
                                        setPageSize(Number(e.target.value));
                                    }}
                                >
                                    {[10, 20, 30, 40, 50].map((pageSize) => (
                                        <option key={pageSize} value={pageSize}>
                                            Show {pageSize}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </>
                    )}
                </Col>
            </Row>
        </>
    )
}

const Sorting = ({column}) => (
    <span className="react-table__column-header sortable">
    {column.isSortedDesc === undefined ? (
        <SortIcon/>
    ) : (
        <span>
        {column.isSortedDesc
            ? <SortAscendingIcon/>
            : <SortDescendingIcon/>}
      </span>
    )}
  </span>
);

export default ProgramViewUserGiftCodesRedeemed;


export const GIFT_CODES_REDEEMED_COLUMNS = [

    {
        Header: "Date",
        accessor: "date"
    },
    {
        Header: "Amount",
        accessor: "amount",
        Cell: ({ row, value }) => {
            // return parseInt(value);
            if(  !row.original.redeemed_conversion_rate || parseInt(row.original.redeemed_conversion_rate) === 1 ) return parseFloat(value).toFixed(2);
            return `${row.original.redeemed_currency_code}${parseFloat(value).toFixed(2)}(~$${row.original.redeemed_conversion_value})`
        },
    },
    {
        Header: "Code",
        accessor: "code",
    },
    {
        Header: "Pin",
        accessor: "pin",
    },
    {
        Header: "Merchant",
        accessor: "name",
        Cell: ({ row, value }) => (
            <div>
                <span>{value}</span>
                {row.original.gcm_merchant_name && (
                    <div style={{ fontSize: '0.82em', color: '#888', marginTop: '2px' }}>
                        GCM: {row.original.gcm_merchant_name}
                    </div>
                )}
            </div>
        ),
    },
]

// Owns its own reason text locally so typing never touches the parent table's
// `columns`/state -- keeping the columns array stable across keystrokes is what
// keeps this input from losing focus (see the comment above buildGiftCodesRedeemedColumns's caller).
const RefundCell = ({row, onRefund}) => {
    const [reason, setReason] = useState('');

    if (row.original.refunded_at) {
        return <span>Refunded {new Date(row.original.refunded_at).toLocaleDateString("en-us")}</span>;
    }
    if (!row.original.redemption_journal_event_id) {
        return <span>Not eligible</span>;
    }
    return (
        <>
            <Input
                type="text"
                placeholder="Reason for refund"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
            />
            <Button
                size="sm"
                color="danger"
                disabled={!reason}
                onClick={() => onRefund(row.original.id, reason)}
            >
                Refund
            </Button>
        </>
    );
};

export const buildGiftCodesRedeemedColumns = ({onRefund}) => [
    ...GIFT_CODES_REDEEMED_COLUMNS,
    {
        Header: "Refund",
        accessor: "refund",
        Cell: ({row}) => <RefundCell row={row} onRefund={onRefund}/>,
    },
]